import { ApmBase, Transaction, Span, init } from '@elastic/apm-rum';
import {
    apmConfig,
    apmMonitorConfig,
    EApmTransactionName,
    EApmSpanName,
    EApmSpanType
} from '@/config/apm';

/**
 * APM monitoring service for manual transaction and span management
 * Provides explicit lifecycle control for instrumentation
 */
class ApmMonitorService {

    private apm: ApmBase | null = null;

    // Two-tier logging system
    private globalLogs: any[] = [];

    private transactionLogs: any[] = [];
    
    private readonly spanRecords: Map<string, Span> = new Map();

    /**
     * Initialize APM and the service
     * @returns The APM instance or null if initialization fails
     */
    public initApm(): ApmBase | null {

        try {

            this.apm = init( apmConfig );
            this.apm.addFilter( this._apmFilter );
            this.apm.observe( 'transaction:end', () => {

                this.transactionLogs = [];
            
            });
            return this.apm;
        
        } catch ( error ) {

            console.error( 'Failed to initialize APM:', error );
            return null;
        
        }
    
    }

    /**
     * Check if APM is available and active
     */
    private isActive(): boolean {

        return this.apm !== null && this.apm !== undefined;
    
    }
     
    /**
     * Update transaction context with additional data
     * @param context - Additional custom context
     */
    public updateContext( context: Record<string, any> ): void {

        this.apm?.getCurrentTransaction()?.addContext({ custom: context });
    
    }

    /**
     * Add a global log entry that persists across transactions
     * @param logs - Log entries to add
     */
    public addGlobalLog( ...logs: any[] ): void {

        this.globalLogs.push( ...logs );
        
        // Trim to max size (keep most recent)
        if ( this.globalLogs.length > apmMonitorConfig.maxGlobalLogs ) {

            this.globalLogs = this.globalLogs.slice( -apmMonitorConfig.maxGlobalLogs );
        
        }
    
    }
    
    /**
     * Add transaction-specific log entry
     * @param logs - Log entries to add
     */
    public addTransactionLog( ...logs: any[] ): void {

        this.transactionLogs.push( ...logs );
        
        // Trim to max size
        if ( this.transactionLogs.length > apmMonitorConfig.maxTransactionLogs ) {

            this.transactionLogs = this.transactionLogs.slice( -apmMonitorConfig.maxTransactionLogs );
        
        }
        
        this.syncLogsToTransaction();
    
    }
    
    /**
     * Sync both global and transaction logs to transaction context
     */
    private syncLogsToTransaction(): void {

        const contextLogs: any = {
            transaction: this.transactionLogs
        };
        
        if ( apmMonitorConfig.includeGlobalLogsInTransaction ) {

            contextLogs.global = this.globalLogs;
        
        }
        
        this.getCurrentTransaction()?.addContext({
            custom: {
                logs: contextLogs
            }
        });
    
    }
     
    private readonly _apmFilter: FilterFn = ( payload ) => {

        const transactions = payload.transactions;
        if ( Array.isArray( transactions ) && transactions.length > 0 ) {

            payload.transactions = transactions.filter(( transaction: Transaction ) => {

                // 过滤掉非 LEVEL 的 Click 事务
                const isNonLevelClick = transaction.name.startsWith( 'Click' ) && !transaction.name.includes( 'LEVEL' );
                // 过滤掉包含 google 的事务
                const containsGoogle = transaction.name.toLocaleLowerCase().includes( 'google' );
    
                return !isNonLevelClick && !containsGoogle;

            });
        
        }
        console.log( 'APM Payload:', payload );
        
        return payload;
    
    };

    /**
     * Add labels to the current transaction
     * @param labels - Labels to add
     */
    public addLabels( labels: Record<string, string> ): void {

        this.getCurrentTransaction()?.addLabels( labels );
    
    }

    /**
     * Start a span manually (must call span.end() manually)
     * @param name - Span name (from ApmSpanName enum)
     * @param type - Span type (from ApmSpanType enum)
     * @returns Span instance or null
     */
    public startSpan( name: EApmSpanName, type: EApmSpanType ): Span | null {

        const span = this.getCurrentTransaction()?.startSpan( name, type, {
            blocking: true
        }) ?? null;
        if ( span ) {

            this.spanRecords.set( name, span );
        
        }

        return span;
    
    }

    /**
     * Capture an error and associate it with the current transaction/span
     * @param error - Error to capture
     * @param context - Additional context
     */
    public captureError( error: Error, context?: Record<string, any> ): void {

        if ( !this.isActive()) {

            return;
        
        }

        this.apm!.captureError( error );

        this.getCurrentTransaction()?.addContext({ custom: context });
    
    }

    /**
     * End the current transaction
     */
    public endTransaction(): void {

        this.getCurrentTransaction()?.end();
    
    }

    /**
     * Check if there's an active transaction
     */
    public hasActiveTransaction(): boolean {

        return this.getCurrentTransaction() !== null;
    
    }

    /**
     * Get the current transaction (for advanced use cases)
     */
    public getCurrentTransaction(): Transaction | null {

        return this.apm?.getCurrentTransaction() ?? null;
    
    }

    public endSpan( name: EApmSpanName ): void {

        const span = this.spanRecords.get( name );
        if ( span ) {

            span.end();
            this.spanRecords.delete( name );
        
        }
    
    }

    public changeTransactionName( name: EApmTransactionName ): void {

        const currentTransaction = this.apm?.getCurrentTransaction();
        if ( currentTransaction ) {

            currentTransaction.name = name;
        
        }
    
    }

    public setTransactionOutcome( outcome: 'success' | 'failure' | 'unknown' ): void {

        const currentTransaction = this.apm?.getCurrentTransaction();
        if ( currentTransaction ) {

            ( currentTransaction as { outcome?: string; }).outcome = outcome;
        
        }
    
    }

    /**
     * 开启或重命名当前事务
     * @param name 
     * @param type 
     */
    public startOrRenameTransaction( name: EApmTransactionName, type: string ): void {

        if ( this.hasActiveTransaction()) {

            this.changeTransactionName( name );
        
        } else {

            this.apm?.startTransaction( name, type );
        
        }
    
    }

}

// Export singleton instance
export const apmMonitor = new ApmMonitorService();
